import { useState, useEffect, useCallback, useMemo } from 'react';
import { i18n, Language } from '../i18n';

export function useTranslation() {
  const [language, setLanguage] = useState<Language>(i18n.getCurrentLanguage());
  const [isReady, setIsReady] = useState(i18n.isInitialized());

  useEffect(() => {
    // Subscribe to language changes
    const unsubscribe = i18n.subscribe(() => {
      setLanguage(i18n.getCurrentLanguage());
      setIsReady(true);
    });

    let checkInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // Initial setup - check if already initialized
    if (i18n.isInitialized()) {
      setLanguage(i18n.getCurrentLanguage());
      setIsReady(true);
    } else {
      // Wait for initialization (mobile only)
      checkInterval = setInterval(() => {
        if (i18n.isInitialized()) {
          setLanguage(i18n.getCurrentLanguage());
          setIsReady(true);
          if (checkInterval) clearInterval(checkInterval);
        }
      }, 10);

      // Cleanup interval after 5 seconds max
      timeoutId = setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
      }, 5000);
    }

    return () => {
      unsubscribe();
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const changeLanguage = useCallback(async (newLanguage: Language) => {
    await i18n.setLanguage(newLanguage);
  }, []);

  const t = useCallback(
    (key: string, defaultOrVars?: string | Record<string, any>, vars?: Record<string, any>) => {
      return i18n.t(key, defaultOrVars as any, vars);
    },
    [language]
  );

  const availableLanguages = useMemo(() => i18n.getAvailableLanguages(), []);
  const getAIPromptPrefix = useCallback(() => i18n.getAIPromptPrefix(), [language]);

  return {
    t,
    language,
    changeLanguage,
    isReady,
    availableLanguages,
    getAIPromptPrefix,
  };
}
